import { useCallback, useMemo } from "react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { API_URL, LoginCredentials } from "../config";

const BASE_URL = API_URL;

function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

async function handleResponse(response: Response) {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const error = new Error(errorData.message || "Error en la API");
    error.name = 'ApiError';
    (error as any).status = response.status;
    (error as any).data = errorData;
    throw error;
  }
  return response.json();
}

async function makeRequest(endpoint: string, options: RequestInit = {}, retries = 2): Promise<any> {
  try {
    const response = await fetch(`${BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        ...getAuthHeaders(),
        ...options.headers,
      },
    });
    return handleResponse(response);
  } catch (error) {
    console.error(`API Error (${endpoint}):`, {
      message: error instanceof Error ? error.message : 'Unknown error',
      endpoint,
      timestamp: new Date().toISOString(),
    });
    
    // If we have retries left and it's a connection error, retry after delay
    if (
      retries > 0 && 
      (
        error instanceof TypeError || 
        (error instanceof Error && 
          (error.message.includes('Failed to fetch') || 
           error.message.includes('NetworkError'))
        )
      )
    ) {
      console.log(`Retrying request to ${endpoint} (${retries} attempts left)...`);
      // Exponential backoff - wait longer with each retry
      const delay = (3 - retries) * 1000; // 1s for first retry, 2s for second
      await new Promise(resolve => setTimeout(resolve, delay));
      return makeRequest(endpoint, options, retries - 1);
    }
    
    throw error;
  }
}

// ----------------------------------------------------
// Tipos Base
// ----------------------------------------------------
export type ReservationStatus = 'pending_payment' | 'partial_payment' | 'paid' | 'no_show' | 'completed';

// ----------------------------------------------------
// Interfaces de Entidades Principales
// ----------------------------------------------------
export interface Excursion {
  id: number;
  name: string;
  description: string;
  priceB2CAdult: number;
  priceB2CChild?: number | null;
  priceB2BAdult: number;
  priceB2BChild?: number | null;
  maxCapacity: number;
  booked: number;
  active: boolean;
  destination: string;
  isThirdParty: boolean;
}

export interface ClienteB2C {
  id: number;
  name: string;
  email: string;
  phone: string;
  active: boolean;
  documentoIdentidad?: string;
  reservations?: number;
}

export interface AgenciaB2B {
  id: number;
  name: string;
  email: string;
  phone: string;
  rncEmpresa?: string;
  responsable?: string;
  active: boolean;
  reservations: number;
}

// ----------------------------------------------------
// Interfaces de Reservas
// ----------------------------------------------------
export interface Payment {
  id: number;
  amount: number;
  date: string;
  method: "efectivo" | "tarjeta" | "transferencia";
  reservationId: number;
  createdAt: string;
  updatedAt: string;
  reservation?: {
    id: number;
    excursion: string;
    fecha: string;
    precioTotal: number;
  };
}

export interface ReservaB2C {
  id: number;
  type: "b2c";
  cliente: number;
  excursion: string;
  fecha: string;       // Fecha formateada para visualización (dd/MM/yyyy)
  fechaISO?: string;   // Fecha en formato ISO (yyyy-mm-dd) para edición
  personas: number;
  adultos: number;
  ninios: number;
  estado: "pending_payment" | "partial_payment" | "paid" | "no_show" | "completed";
  hotel: string;
  hotelData?: {
    nombre: string;
    horarioRecogida?: string | null;
    lugarRecogida?: string | null;
  } | null;
  precioTotal: number;
  metodoPago: "efectivo" | "tarjeta" | "transferencia";
  notas?: string;
  descuento?: number;
  montoFacturado: number;
  saldoPendiente: number;
  clientId?: number | null;
  clienteName?: string;
  fechaPago?: string;  // Fecha del último pago
  payments?: Payment[];
  abono?: number;      // Monto inicial abonado
  taxRate?: number;    // Tasa de impuesto aplicada a la reserva
  isThirdParty?: boolean; // Indica si la excursión es de terceros
  incluyeSeguro?: boolean; // Indica si la reserva incluye seguro de viaje
}

export interface ReservaB2B {
  id: number;
  type: "b2b";
  agencia: number;
  excursion: string;
  fecha: string;
  fechaISO?: string;
  personas: number;
  adultos: number;
  ninios: number;
  estado: "pending_payment" | "partial_payment" | "paid" | "no_show" | "completed";
  hotel: string;
  hotelData?: {
    nombre: string;
    horarioRecogida?: string | null;
    lugarRecogida?: string | null;
  } | null;
  precioTotal: number;
  metodoPago: "efectivo" | "tarjeta" | "transferencia";
  notas?: string;
  descuento?: number;
  montoFacturado: number;
  saldoPendiente: number;
  agencyId?: number | null;
  agenciaName?: string;
  clientEndName?: string;  // Nuevo campo para el nombre del cliente final
  taxRate?: number;    // Tasa de impuesto aplicada a la reserva
  isThirdParty?: boolean; // Indica si la excursión es de terceros
  agenciaComision?: number; // Comisión fija que se lleva la agencia
  incluyeSeguro?: boolean; // Indica si la reserva incluye seguro de viaje
}

export type Reserva = ReservaB2C | ReservaB2B;

// ----------------------------------------------------
// Interfaces de Análisis y Notificaciones
// ----------------------------------------------------
interface Reservation {
  type: 'b2c' | 'b2b';
  clienteName?: string;
  agenciaName?: string;
  precioTotal: number;
}

interface AgencyMetrics {
  bookings: number;
  revenue: number;
}

interface CalculatePriceParams {
  type: "b2c" | "b2b";
  excursion: string;
  adultos: number;
  ninios: number;
  descuento: number;
  taxRate?: number;
  agencia?: number;
  agenciaComision?: number;
  incluyeSeguro?: boolean;
}

interface UpcomingTour {
  name: string;
  date: string;
  bookings: number;
  totalPax: number;
}

export interface UpcomingTourNotification {
  id: number;
  excursion: string;
  fecha: string;
  guestName: string;
  estado: ReservationStatus;
  adultos: number;
  ninios: number;
  totalPax: number;
}

export interface Notification {
  id: number;
  excursion: string;
  fecha: string;
  fechaNotificacion: string;
  guestName: string;
  estado: ReservationStatus;
  adultos: number;
  ninios: number;
  totalPax: number;
  leido: boolean;
}

// ----------------------------------------------------
// Interfaces de Facturación
// ----------------------------------------------------
export interface LineaFactura {
  id?: number;
  descripcion: string;
  cantidad: number;
  precioUnitario: number;
  impuesto: number;
  subtotal: number;
  total: number;
  hotel?: string;
  fechaServicio?: string | null;
  tipoServicio?: string;
  destino?: string;
  costoProveedor?: number;
  comision?: number;
}

export interface Factura {
  id?: number;
  numero: string;
  titulo?: string;
  fecha: string;
  clientId: number;
  clientType?: 'individual' | 'empresa';
  estado: 'pendiente' | 'pagada' | 'anulada' | 'abonada';
  lineas: LineaFactura[];
  subtotal: number;
  impuestoTotal: number;
  total: number;
  abono?: number;
  saldoPendiente?: number;
  notas?: string;
  entregadoPor?: string;
  recibidoPor?: string;
  clienteNombre?: string;
  manualInvoice?: boolean;
  clienteNombreManual?: string;
  reservationId?: number;
  reservation?: {
    id: number;
    descuento: number;
    taxRate: number;
    type?: 'b2c' | 'b2b';
    agenciaComision?: number;
  } | null;
}

// Añadir esta interfaz con las otras interfaces
export interface FacturaStats {
  totalFacturas: number;
  totalIngresos: number;
  pendientesCobro: number;
  clientesActivos: number;
  porEstado: {
    pendientes: number;
    pagadas: number;
    anuladas: number;
    abonadas: number;
  };
  tendencias: {
    ingresos: number;
    pendientes: number;
    clientes: number;
    facturas: number;
  };
}

export interface DuplicateReservationCheckResult {
  success: boolean;
  data: Array<{
    id: number;
    fecha: string;
    clientName: string;
    finalClientName: string | null;
    adultos: number;
    ninios: number;
    hotel: string;
    hotelData?: {
      nombre: string;
      horarioRecogida?: string | null;
      lugarRecogida?: string | null;
    } | null;
    status: ReservationStatus;
    excursion: string;
  }>;
  count: number;
}

export interface IngresoStats {
  totalIngresos: number;
  ingresosDelMes: number;
  clientesConPagos: number;
  porEstado: {
    completados: number;
    parciales: number;
  };
  tendencias: {
    ingresos: number;
    clientes: number;
  };
}

export interface Ingreso {
  id: number;
  fecha: string;
  clienteNombre: string;
  monto: number;
  estado: string;
  facturaNumero?: string;
  metodoPago?: string;
}

export interface ExportFilters {
  startDate: string;
  endDate: string;
  cliente: string;
  clientId?: number;
}

export interface SyncResult {
  ingresosCreated: number;
  ingresosDeleted: number;
  errors: number;
}

// ----------------------------------------------------
// Notas de Crédito/Débito
// ----------------------------------------------------
export interface Nota {
  id?: number;
  fecha: string;
  facturaId: number;
  facturaNumero?: string;
  tipo: 'credito' | 'debito';
  numero: string;
  monto: number;
  motivo: 'error' | 'descuento' | 'ajuste' | 'otro';
  descripcion: string;
  estado: 'aplicada' | 'anulada';
  clienteNombre?: string;
  motivoDetalle?: string;
  impactoSaldo?: number;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface NotaStats {
  totalNotas: number;
  notasDelMes: number;
  porTipo: {
    credito: number;
    debito: number;
  };
  porMotivo: {
    error: number;
    descuento: number;
    ajuste: number;
    otro: number;
  };
  porEstado: {
    aplicadas: number;
    anuladas: number;
  };
  totalCredito: number;
  totalDebito: number;
  tendencias?: {
    notas: number;
  };
}

export interface ExportNotasFilters {
  startDate: string;
  endDate: string;
  tipo?: string;
  motivo?: string;
  facturaNumero?: string;
  estado?: string;
}

// ----------------------------------------------------
// Gasto
// ----------------------------------------------------
export interface Gasto {
  id?: number;
  fecha: string;
  monto: number;
  proveedor: string;
  descripcion: string;
  categoria: string;
  metodoPago: string;
  comprobante?: string;
  notas?: string;
  createdAt?: Date;
  updatedAt?: Date;
  concepto?: string;
  impuesto?: number;
  total?: number;
  proveedorId?: number;
  proveedorNombre?: string;
  esFrecuente?: boolean;
}

export interface GastoFormData {
  fecha: string;
  monto: number;
  proveedor: string;
  descripcion: string;
  categoria: string;
  metodoPago: string;
  comprobante?: string;
  notas?: string;
}

export interface GastoStats {
  totalGastos: number;
  gastosDelMes: number;
  gastosDelMesAnterior: number;
  porCategoria: {
    [key: string]: number;
  };
  tendencias: {
    gastos: number;
  };
  totalProveedores: number;
}

export interface ExportGastosFilters {
  startDate: string;
  endDate: string;
  categoria: string;
  proveedor: string;
}

// ----------------------------------------------------
// Reporting / Estadísticas
// ----------------------------------------------------
export interface VentasReservasStats {
  ingresosTotales: number;
  numeroReservas: number;
  ticketPromedio: number;
  tasaCancelacion: number;
  reservasTipoCliente: {
    b2c: number;
    b2b: number;
  };
  excursionMasVendida: string;
  temporadaMayorVenta: string;
  reservasPorMes: number[];
  ingresosPorMes: number[];
  tendencia: number;
}

export interface GastosRentabilidadStats {
  gastosTotales: number;
  costoPromedioExcursion: number;
  rentabilidadPromedio: number;
  flujoEfectivoNeto: number;
  excursionMasRentable: {
    nombre: string;
    rentabilidad: number;
  };
  saldoPendienteCobro: number;
  distribucionGastos: Array<{
    categoria: string;
    monto: number;
    color: string;
  }>;
  gastosPorMes: number[];
  rentabilidadPorMes: number[];
  tendenciaGastos: number;
  tendenciaCostoPromedio: number;
  tendenciaRentabilidad: number;
  tendenciaFlujoEfectivo: number;
  tendenciaSaldoPendiente: number;
  mesMasGastos: string;
  mesMasRentable: string;
}

// ----------------------------------------------------
// Utilidades
// ----------------------------------------------------
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

const formatMetodoPago = (metodo?: string): string => {
  if (!metodo) return "No especificado";
  
  const metodosMap: Record<string, string> = {
    "efectivo": "Efectivo",
    "tarjeta": "Tarjeta",
    "transferencia": "Transferencia bancaria",
    "no_especificado": "No especificado",
    "automatico": "Registro automático",
    "cheque": "Cheque",
    "deposito": "Depósito bancario",
    "credito": "Crédito",
    "otro": "Otro método"
  };
  
  return metodosMap[metodo] || metodo;
};

// ----------------------------------------------------
// Hook Principal
// ----------------------------------------------------
const useApi = () => {
  // Autenticación
  const authMethods = useMemo(() => ({
    login: async (credentials: LoginCredentials) => {
      return makeRequest('/auth/login', {
        method: 'POST',
        body: JSON.stringify(credentials),
      });
    },
    updateProfile: async (userData: any) => {
      return makeRequest('/user/profile', {
        method: 'PUT',
        body: JSON.stringify(userData),
      });
    }
  }), []);

  // Excursiones
  const excursionMethods = useMemo(() => ({
    getExcursiones: async () => {
      return makeRequest('/excursions/list');
    },
    createExcursion: async (excursion: Omit<Excursion, "id" | "booked">) => {
      return makeRequest('/excursions', {
        method: 'POST',
        body: JSON.stringify(excursion),
      });
    },
    updateExcursion: async (updatedExcursion: Excursion) => {
      return makeRequest('/excursions/' + updatedExcursion.id, {
        method: 'PUT',
        body: JSON.stringify(updatedExcursion),
      });
    },
    deleteExcursion: async (id: number) => {
      return makeRequest('/excursions/' + id, {
        method: 'DELETE',
      });
    }
  }), []);

  // Dashboard y Métricas
  const dashboardMethods = useMemo(() => ({
    fetchMetrics: async () => {
      return makeRequest('/dashboard/metrics');
    },
    fetchRecentBookings: async () => {
      return makeRequest('/dashboard/recent-bookings');
    },
    fetchUpcomingTours: async (): Promise<UpcomingTour[]> => {
      try {
        const reservas = await makeRequest('/reservations');
        
        // Obtener la fecha actual al inicio del día
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Filtrar y agrupar reservas futuras por excursión y fecha
        const upcomingReservas = (reservas as Array<ReservaB2C | ReservaB2B>)
          .filter((reserva) => {
            // Convertir fecha de dd/mm/yyyy a Date
            const [day, month, year] = reserva.fecha.split('/');
            const reservaDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
            reservaDate.setHours(0, 0, 0, 0);

            // Solo incluir reservas futuras y con estados válidos
            return reservaDate >= today && 
                   ['pending_payment', 'partial_payment', 'paid'].includes(reserva.estado);
          })
          .reduce((acc: Record<string, UpcomingTour>, reserva) => {
            const key = `${reserva.excursion}_${reserva.fecha}`;
            
            if (!acc[key]) {
              acc[key] = {
                name: reserva.excursion,
                date: reserva.fecha,
                bookings: 0,
                totalPax: 0
              };
            }
            
            acc[key].bookings += 1;
            acc[key].totalPax += (reserva.adultos + reserva.ninios);
            
            return acc;
          }, {});

        // Convertir a array y ordenar por fecha
        return (Object.values(upcomingReservas) as UpcomingTour[])
          .sort((a, b) => {
            const [dayA, monthA, yearA] = a.date.split('/');
            const [dayB, monthB, yearB] = b.date.split('/');
            const dateA = new Date(parseInt(yearA), parseInt(monthA) - 1, parseInt(dayA));
            const dateB = new Date(parseInt(yearB), parseInt(monthB) - 1, parseInt(dayB));
            return dateA.getTime() - dateB.getTime();
          })
          .slice(0, 5); // Mostrar solo las próximas 5 salidas
      } catch (error) {
        console.error('Error fetching upcoming tours:', error);
        return [];
      }
    },
    fetchTourFirstReservation: async (tourName: string, tourDate: string): Promise<ReservaB2C | ReservaB2B | null> => {
      try {
        // Fetch all reservations
        const allReservas = await makeRequest('/reservations') as Array<ReservaB2C | ReservaB2B>;
        
        // Find the first active reservation for this tour and date
        const reservation = allReservas.find(
          (reserva) => 
            reserva.excursion === tourName && 
            reserva.fecha === tourDate && 
            ['pending_payment', 'partial_payment', 'paid'].includes(reserva.estado)
        );
        
        return reservation || null;
      } catch (error) {
        console.error('Error fetching tour reservation:', error);
        return null;
      }
    },
    fetchTourAllReservations: async (tourName: string, tourDate: string): Promise<Array<ReservaB2C | ReservaB2B>> => {
      try {
        // Fetch all reservations
        const allReservas = await makeRequest('/reservations') as Array<ReservaB2C | ReservaB2B>;
        
        // Filter all active reservations for this tour and date
        const reservations = allReservas.filter(
          (reserva) => 
            reserva.excursion === tourName && 
            reserva.fecha === tourDate && 
            ['pending_payment', 'partial_payment', 'paid'].includes(reserva.estado)
        );
        
        return reservations;
      } catch (error) {
        console.error('Error fetching tour reservations:', error);
        return [];
      }
    },
    fetchUpcomingTourNotifications: async (): Promise<UpcomingTourNotification[]> => {
      try {
        const reservas = await makeRequest('/reservations');
        
        // Obtener la fecha actual
        const now = new Date();
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(23, 59, 59, 999);

        // Filtrar reservas para tours en las próximas 24 horas
        const upcomingTours = (reservas as Array<ReservaB2C | ReservaB2B>)
          .filter((reserva) => {
            const [day, month, year] = reserva.fecha.split('/');
            const reservaDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
            
            return reservaDate <= tomorrow && 
                   reservaDate >= now && 
                   ['paid', 'partial_payment'].includes(reserva.estado);
          })
          .map((reserva) => ({
            id: reserva.id,
            excursion: reserva.excursion,
            fecha: reserva.fecha,
            guestName: 'type' in reserva && reserva.type === 'b2c' 
              ? reserva.clienteName || 'Cliente sin nombre'
              : reserva.agenciaName || 'Agencia sin nombre',
            estado: reserva.estado,
            adultos: reserva.adultos,
            ninios: reserva.ninios,
            totalPax: reserva.adultos + reserva.ninios
          }));

        return upcomingTours;
      } catch (error) {
        console.error('Error fetching upcoming tours:', error);
        return [];
      }
    },
    fetchAnalysisMetrics: async () => {
      return makeRequest('/analysis/metrics');
    }
  }), []);

  // Clientes
  const clientMethods = useMemo(() => ({
    getClients: async () => {
      return makeRequest('/clients');
    },
    createClient: async (newClient: Omit<ClienteB2C, "id" | "reservations">) => {
      return makeRequest('/clients', {
        method: 'POST',
        body: JSON.stringify(newClient),
      });
    },
    updateClient: async (updatedClient: ClienteB2C) => {
      return makeRequest('/clients/' + updatedClient.id, {
        method: 'PUT',
        body: JSON.stringify(updatedClient),
      });
    },
    deleteClient: async (id: number) => {
      return makeRequest('/clients/' + id, {
        method: 'DELETE',
      });
    },
    getClientById: async (id: number) => {
      return makeRequest('/clients/' + id);
    },
    getClientHistory: async (id: number) => {
      return makeRequest('/clients/' + id + '/history');
    }
  }), []);

  // Agencias
  const agencyMethods = useMemo(() => ({
    getAgencies: async () => {
      return makeRequest('/agencies');
    },
    createAgency: async (agencyData: Omit<AgenciaB2B, "id" | "reservations">) => {
      const response = await makeRequest('/agencies', {
        method: 'POST',
        body: JSON.stringify(agencyData),
      });
      return response as AgenciaB2B;
    },
    updateAgency: async (updatedAgency: AgenciaB2B) => {
      return makeRequest('/agencies/' + updatedAgency.id, {
        method: 'PUT',
        body: JSON.stringify(updatedAgency),
      });
    },
    deleteAgency: async (id: number) => {
      return makeRequest('/agencies/' + id, {
        method: 'DELETE',
      });
    },
    getAgencyById: async (id: number) => {
      return makeRequest('/agencies/' + id);
    },
    getAgencyReservations: async (id: number) => {
      return makeRequest('/agencies/' + id + '/reservations');
    }
  }), []);

  // Extract the processBookingTrends function outside the hook to avoid nesting useMemo
  const processBookingTrends = (bookings: any[]): { name: string; data: number[] }[] => {
    if (!bookings || bookings.length === 0) return [];
    
    console.log(`Procesando ${bookings.length} reservas para tendencias`);
    
    const currentYear = new Date().getFullYear();
    
    // Crear estructura para los meses
    const monthsData = Array.from({ length: 12 }, (_, index) => ({
      month: index + 1,
      year: currentYear,
      count: 0
    }));

    // Procesar todas las reservas y agruparlas por mes
    bookings.forEach(booking => {
      // Formato esperado: DD/MM/YYYY
      if (booking.date && typeof booking.date === 'string') {
        const [day, month, year] = booking.date.split('/');
        const bookingMonth = parseInt(month);
        const bookingYear = parseInt(year);
        
        // Solo contar reservas del año actual
        if (bookingYear === currentYear && bookingMonth >= 1 && bookingMonth <= 12) {
          monthsData[bookingMonth - 1].count++;
        }
      }
    });

    // Convertir a formato esperado por el gráfico
    const bookingCounts = monthsData.map(month => month.count);
    
    console.log('Datos de tendencia procesados:', bookingCounts);

    return [{
      name: "Reservas",
      data: bookingCounts
    }];
  };

  // Reservas
  const reservationMethods = useMemo(() => ({
    getReservas: async () => {
      return makeRequest('/reservations');
    },
    createReserva: async (newReserva: Omit<ReservaB2C, "id"> | Omit<ReservaB2B, "id">) => {
      return makeRequest('/reservations', {
        method: 'POST',
        body: JSON.stringify(newReserva),
      });
    },
    updateReserva: async (updatedReserva: ReservaB2C | ReservaB2B) => {
      return makeRequest('/reservations/' + updatedReserva.id, {
        method: 'PUT',
        body: JSON.stringify(updatedReserva),
      });
    },
    deleteReserva: async (id: number) => {
      return makeRequest('/reservations/' + id, {
        method: 'DELETE',
      });
    },
    registerPayment: async (id: number, amount: number, method: "efectivo" | "tarjeta" | "transferencia" = "efectivo", justificacionExtra?: string) => {
      return makeRequest('/reservations/' + id + '/payment', {
        method: 'PUT',
        body: JSON.stringify({ amount, method, justificacionExtra }),
      });
    },
    getReservationPayments: async (id: number): Promise<Payment[]> => {
      return makeRequest('/reservations/' + id + '/payments');
    },
    getClientPayments: async (id: number): Promise<Payment[]> => {
      return makeRequest('/reservations/client/' + id + '/payments');
    },
    getAgencyPayments: async (id: number): Promise<Payment[]> => {
      return makeRequest('/reservations/agency/' + id + '/payments');
    },
    calculatePrice: async ({
      type,
      excursion,
      adultos,
      ninios,
      descuento = 0,
      taxRate = 18,
      agencia,
      agenciaComision = 0,
      incluyeSeguro = false
    }: {
      type: "b2c" | "b2b";
      excursion: string;
      adultos: number;
      ninios: number;
      descuento?: number;
      taxRate?: number;
      agencia?: number;
      agenciaComision?: number;
      incluyeSeguro?: boolean;
    }): Promise<number | { price: number; details?: any }> => {
      try {
        const data = await makeRequest("/reservations/calculate-price", {
          method: "POST",
          body: JSON.stringify({
            type,
            excursion,
            adultos,
            ninios,
            descuento,
            taxRate,
            agencia,
            agenciaComision,
            incluyeSeguro
          })
        });

        // If the response has a price property and it's a number, it's valid
        if (typeof data.price !== 'number') {
          throw new Error('Precio inválido recibido del servidor');
        }

        // Return the complete response with details if available
        if (data.details) {
          return {
            price: data.price,
            details: data.details
          };
        }
        
        // For backward compatibility, return just the price if no details
        return data.price;
      } catch (error) {
        console.error("Error calculando precio:", error);
        throw error;
      }
    },
    processBookingTrends
  }), []);

  // Facturación
  const invoiceMethods = useMemo(() => ({
    getFacturas: async (): Promise<Factura[]> => {
      return makeRequest('/facturas');
    },

    getFactura: async (id: number): Promise<Factura> => {
      return makeRequest(`/facturas/${id}`);
    },

    createFactura: async (factura: Omit<Factura, 'id'>): Promise<Factura> => {
      return makeRequest('/facturas', {
        method: 'POST',
        body: JSON.stringify(factura)
      });
    },

    updateFactura: async (id: number, factura: Partial<Factura>): Promise<Factura> => {
      return makeRequest(`/facturas/${id}`, {
        method: 'PUT',
        body: JSON.stringify(factura)
      });
    },

    deleteFactura: async (id: number): Promise<void> => {
      try {
        const response = await fetch(`${BASE_URL}/facturas/${id}`, {
          method: 'DELETE',
          headers: {
            ...getAuthHeaders()
          }
        });
        
        if (!response.ok) {
          const errorData = await response.text();
          const error = new Error(`Error al eliminar la factura: ${errorData}`);
          (error as any).status = response.status;
          throw error;
        }
        
        // Success with no content (204) - don't try to parse as JSON
        return;
      } catch (error) {
        console.error('Error deleting factura:', error);
        throw error;
      }
    },

    // Nuevos métodos
    getFacturaStats: async (): Promise<FacturaStats> => {
      try {
        const response = await makeRequest('/facturas/stats');
        return response;
      } catch (error) {
        console.error('Error obteniendo estadísticas de facturas:', error);
        // Devolver valores por defecto en caso de error
        return {
          totalFacturas: 0,
          totalIngresos: 0,
          pendientesCobro: 0,
          clientesActivos: 0,
          porEstado: {
            pendientes: 0,
            pagadas: 0,
            anuladas: 0,
            abonadas: 0
          },
          tendencias: {
            ingresos: 0,
            pendientes: 0,
            clientes: 0,
            facturas: 0
          }
        };
      }
    },

    exportFacturas: async (filters?: { 
      startDate?: string; 
      endDate?: string; 
      estado?: 'pendiente' | 'pagada' | 'anulada' | 'todas';
      clientId?: number;
    }) => {
      try {
        // Construir la URL con los filtros si están presentes
        let url = `${BASE_URL}/facturas/export`;
        if (filters) {
          const params = new URLSearchParams();
          if (filters.startDate) params.append('startDate', filters.startDate);
          if (filters.endDate) params.append('endDate', filters.endDate);
          if (filters.estado && filters.estado !== 'todas') params.append('estado', filters.estado);
          if (filters.clientId) params.append('clientId', filters.clientId.toString());
          
          if (params.toString()) {
            url += `?${params.toString()}`;
          }
        }
        
        const response = await fetch(url, {
          method: 'GET',
          headers: getAuthHeaders()
        });
        
        if (!response.ok) {
          if (response.headers.get('content-type')?.includes('application/json')) {
            const errorData = await response.json();
            throw new ApiError(errorData.message || 'Error al exportar facturas', response.status, errorData);
          } else {
            const errorText = await response.text();
            throw new ApiError(errorText || 'Error al exportar facturas', response.status);
          }
        }
        
        // Verificar que la respuesta es un blob
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')) {
          return response.blob();
        } else {
          throw new ApiError('Formato de respuesta inválido', response.status);
        }
      } catch (error) {
        console.error('Error en exportFacturas:', error);
        throw error;
      }
    },

    generateFacturaReport: async (id: number) => {
      const response = await fetch(`${BASE_URL}/facturas/${id}/report`, {
        ...getAuthHeaders(),
        method: 'GET'
      });
      return response.blob();
    },

    // Custom prices functions
    getAgencyCustomPrices: async (agencyId: number): Promise<any[]> => {
      const response = await makeRequest(`/agency-prices/agency/${agencyId}`);
      return response;
    },

    // Add this new function for fixing factura custom prices
    fixFacturaCustomPrices: async (facturaId: number): Promise<any> => {
      return makeRequest(`/facturas/${facturaId}/update-prices`, {
        method: 'POST'
      });
    }
  }), []);

  // Notificaciones
  const notificationMethods = useMemo(() => ({
    getNotifications: async (): Promise<Notification[]> => {
      return makeRequest('/notifications');
    },
    markNotificationAsRead: async (id: number) => {
      return makeRequest(`/notifications/${id}/read`, {
        method: 'PUT'
      });
    },
    deleteNotification: async (id: number) => {
      return makeRequest(`/notifications/${id}`, {
        method: 'DELETE'
      });
    }
  }), []);

  // Hoteles y Analytics
  const utilityMethods = useMemo(() => ({
    getHotels: async () => {
      return makeRequest('/hotels');
    },
    getAnalytics: async () => {
      try {
        const reservations = await makeRequest('/reservations') as Reservation[];
        
        const clientPayments = reservations
          .filter((r) => r.type === 'b2c')
          .reduce((acc: Record<string, number>, r) => {
            const clientName = r.clienteName || 'Unknown';
            acc[clientName] = (acc[clientName] || 0) + r.precioTotal;
            return acc;
          }, {});

        const sortedClients = Object.entries(clientPayments)
          .sort(([, a], [, b]) => b - a);

        const agencyBookings = reservations
          .filter((r) => r.type === 'b2b')
          .reduce((acc: Record<string, AgencyMetrics>, r) => {
            const agencyName = r.agenciaName || 'Unknown';
            if (!acc[agencyName]) {
              acc[agencyName] = { bookings: 0, revenue: 0 };
            }
            acc[agencyName].bookings++;
            acc[agencyName].revenue += r.precioTotal;
            return acc;
          }, {});

        const topAgency = Object.entries(agencyBookings)
          .sort(([, a], [, b]) => b.revenue - a.revenue)[0] as [string, AgencyMetrics];

        return {
          mostPaid: sortedClients[0]
            ? `${sortedClients[0][0]} (${formatCurrency(sortedClients[0][1])})`
            : "Sin datos",
          leastPaid: sortedClients[sortedClients.length - 1]
            ? `${sortedClients[sortedClients.length - 1][0]} (${formatCurrency(sortedClients[sortedClients.length - 1][1])})`
            : "Sin datos",
          topAgencyMetric: topAgency
            ? `${topAgency[0]} (${topAgency[1].bookings} reservas - ${formatCurrency(topAgency[1].revenue)})`
            : "Sin datos"
        };
      } catch (error) {
        console.error('Error getting analytics:', error);
        return {
          mostPaid: "Error al cargar",
          leastPaid: "Error al cargar",
          topAgencyMetric: "Error al cargar"
        };
      }
    }
  }), []);

  const ingresoMethods = useMemo(() => ({
    getIngresos: async (): Promise<Ingreso[]> => {
      return makeRequest('/ingresos');
    },
    
    getIngreso: async (id: number): Promise<Ingreso> => {
      return makeRequest(`/ingresos/${id}`);
    },
    
    createIngreso: async (ingreso: Omit<Ingreso, "id">): Promise<Ingreso> => {
      return makeRequest('/ingresos', {
        method: 'POST',
        body: JSON.stringify(ingreso),
      });
    },
    
    updateIngreso: async (updatedIngreso: Ingreso): Promise<Ingreso> => {
      return makeRequest(`/ingresos/${updatedIngreso.id}`, {
        method: 'PUT',
        body: JSON.stringify(updatedIngreso),
      });
    },
    
    deleteIngreso: async (id: number): Promise<void> => {
      return makeRequest(`/ingresos/${id}`, {
        method: 'DELETE',
      });
    },
    
    getIngresoStats: async (): Promise<IngresoStats> => {
      return makeRequest('/ingresos/stats');
    },
    
    exportIngresos: async (filters: ExportFilters): Promise<Blob> => {
      try {
        // Convert filters to query parameters
        const queryParams = new URLSearchParams();
        
        if (filters.startDate) queryParams.append('startDate', filters.startDate);
        if (filters.endDate) queryParams.append('endDate', filters.endDate);
        if (filters.cliente) queryParams.append('cliente', filters.cliente);
        if (filters.clientId) queryParams.append('clientId', filters.clientId.toString());
        
        const authHeaders = getAuthHeaders();
        const response = await fetch(`${BASE_URL}/ingresos/export?${queryParams.toString()}`, {
          method: 'GET',
          headers: authHeaders
        });
        
        if (!response.ok) {
          if (response.headers.get('content-type')?.includes('application/json')) {
            const errorData = await response.json();
            throw new ApiError(errorData.message || 'Error al exportar ingresos', response.status, errorData);
          } else {
            const errorText = await response.text();
            throw new ApiError(errorText || 'Error al exportar ingresos', response.status);
          }
        }
        
        return response.blob();
      } catch (error) {
        throw error;
      }
    },
    
    syncIngresos: async (): Promise<SyncResult> => {
      return makeRequest('/ingresos/sync', {
        method: 'POST'
      });
    }
  }), []);

  // Add these new functions
  const getClientes = async (): Promise<ClienteSimple[]> => {
    return makeRequest('/clients');
  };

  const getAgencias = async (): Promise<AgenciaSimple[]> => {
    return makeRequest('/agencies');
  };

  // Notas de Crédito/Débito API
  const getNotas = async (): Promise<Nota[]> => {
    const response = await makeRequest('/notas');
    return response;
  };

  const getNotasByFactura = async (facturaId: number): Promise<Nota[]> => {
    const response = await makeRequest(`/notas/factura/${facturaId}`);
    return response;
  };

  const getNotaStats = async (): Promise<NotaStats> => {
    const response = await makeRequest('/notas/stats');
    return response;
  };

  const createNota = async (notaData: Partial<Nota>): Promise<Nota> => {
    const response = await makeRequest('/notas', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(notaData),
    });
    return response;
  };

  const updateNota = async (id: number, notaData: Partial<Nota>): Promise<Nota> => {
    const response = await makeRequest(`/notas/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(notaData),
    });
    return response;
  };

  const deleteNota = async (id: number): Promise<void> => {
    await makeRequest(`/notas/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
  };

  const exportNotas = async (filters: ExportNotasFilters): Promise<void> => {
    const queryParams = new URLSearchParams();
    
    if (filters.startDate) queryParams.append('startDate', filters.startDate);
    if (filters.endDate) queryParams.append('endDate', filters.endDate);
    if (filters.tipo) queryParams.append('tipo', filters.tipo);
    if (filters.motivo) queryParams.append('motivo', filters.motivo);
    if (filters.facturaNumero) queryParams.append('facturaNumero', filters.facturaNumero);
    if (filters.estado) queryParams.append('estado', filters.estado);
    
    const queryString = queryParams.toString();
    const url = `/notas/export?${queryString}`;
    
    window.open(`${API_URL}${url}`, '_blank');
  };

  // Gasto API
  const getGastos = async (filters = {}): Promise<Gasto[]> => {
    const queryParams = new URLSearchParams();
    
    // Add any filters to the query string
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, String(value));
      }
    });
    
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const response = await makeRequest(`/gastos${queryString}`);
    return response.data || [];
  };

  const getGastoStats = async (): Promise<GastoStats> => {
    try {
      const response = await makeRequest('/gastos/stats');
      return response.stats;
    } catch (error) {
      console.error('Error fetching gasto stats:', error);
      throw error;
    }
  };

  const createGasto = async (gastoData: GastoFormData): Promise<Gasto> => {
    const response = await makeRequest('/gastos', {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(gastoData),
    });
    return response.data;
  };

  const updateGasto = async (id: number, gastoData: Partial<GastoFormData>): Promise<Gasto> => {
    const response = await makeRequest(`/gastos/${id}`, {
      method: 'PUT',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(gastoData),
    });
    return response.data;
  };

  const deleteGasto = async (id: number): Promise<void> => {
    await makeRequest(`/gastos/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
  };

  const exportGastos = async (filters: ExportGastosFilters): Promise<Response> => {
    const queryParams = new URLSearchParams({
      startDate: filters.startDate,
      endDate: filters.endDate,
      ...(filters.categoria && { categoria: filters.categoria }),
      ...(filters.proveedor && { proveedor: filters.proveedor })
    });

    const response = await fetch(`${API_URL}/gastos/export?${queryParams}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Error al exportar gastos');
    }

    return response;
  };

  // ----------------------------------------------------
  // Reporting / Estadísticas
  // ----------------------------------------------------
  const reportingMethods = useMemo(() => ({
    getVentasReservasStats: async (
      startDate?: string,
      endDate?: string
    ): Promise<VentasReservasStats> => {
      try {
        // Get current date if dates not provided
        const now = new Date();
        const defaultEndDate = format(now, 'yyyy-MM-dd');
        
        // Default to 1 year ago if not provided
        const defaultStartDate = format(
          new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()),
          'yyyy-MM-dd'
        );

        // Build query params
        const params = new URLSearchParams();
        params.append('startDate', startDate || defaultStartDate);
        params.append('endDate', endDate || defaultEndDate);
        
        // Fetch all required data in parallel
        const [
          totalIncomeData,
          totalReservationsData,
          averageTicketData,
          cancellationRateData,
          clientTypeData,
          topExcursionsData,
          peakSeasonsData
        ] = await Promise.all([
          makeRequest(`/reporting/income?${params.toString()}`),
          makeRequest(`/reporting/reservations?${params.toString()}`),
          makeRequest(`/reporting/average-ticket?${params.toString()}`),
          makeRequest(`/reporting/cancellation-rate?${params.toString()}`),
          makeRequest(`/reporting/reservations-by-client-type?${params.toString()}`),
          makeRequest(`/reporting/top-excursions?${params.toString()}`),
          makeRequest(`/reporting/peak-seasons?${params.toString()}`)
        ]);

        // Process monthly data for charts
        // Generate array for past 12 months
        const months = Array.from({ length: 12 }, (_, i) => {
          const d = new Date();
          d.setMonth(d.getMonth() - 11 + i);
          return format(d, 'yyyy-MM');
        });

        // Get monthly reservations and income
        // In a real implementation, these would be from API responses
        // For simplicity, we'll populate with the data from response or mock data
        const reservasPorMes = months.map(month => {
          const monthData = totalReservationsData?.data?.byMonth?.find(
            (m: any) => m.month === month.split('-')[1] && m.year === month.split('-')[0]
          );
          return monthData ? monthData.count : 0;
        });

        const ingresosPorMes = months.map(month => {
          const monthData = totalIncomeData?.data?.byMonth?.find(
            (m: any) => m.month === month.split('-')[1] && m.year === month.split('-')[0]
          );
          return monthData ? monthData.total : 0;
        });

        // Calculate trend (percentage change from previous period)
        const currentPeriodIncome = totalIncomeData?.data?.totalIncome || 0;
        const previousPeriodIncome = totalIncomeData?.data?.previousPeriodIncome || 0;
        const tendencia = previousPeriodIncome > 0
          ? ((currentPeriodIncome - previousPeriodIncome) / previousPeriodIncome) * 100
          : 0;

        return {
          ingresosTotales: totalIncomeData?.data?.totalIncome || 0,
          numeroReservas: totalReservationsData?.data?.totalReservations || 0,
          ticketPromedio: averageTicketData?.data?.averageTicket || 0,
          tasaCancelacion: cancellationRateData?.data?.cancellationRate || 0,
          reservasTipoCliente: {
            b2c: clientTypeData?.data?.byType?.b2c || 0,
            b2b: clientTypeData?.data?.byType?.b2b || 0
          },
          excursionMasVendida: topExcursionsData?.data?.excursions?.[0]?.name || "Sin datos",
          temporadaMayorVenta: peakSeasonsData?.data?.seasons?.[0]?.name || "Sin datos",
          reservasPorMes,
          ingresosPorMes,
          tendencia: Math.round(tendencia * 10) / 10 // Round to 1 decimal place
        };
      } catch (error) {
        console.error("Error obteniendo estadísticas de ventas y reservas:", error);
        throw error;
      }
    },
    getGastosRentabilidadStats: async (
      startDate?: string,
      endDate?: string
    ): Promise<GastosRentabilidadStats> => {
      try {
        // Get current date if dates not provided
        const now = new Date();
        const defaultEndDate = format(now, 'yyyy-MM-dd');
        
        // Default to 1 year ago if not provided
        const defaultStartDate = format(
          new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()),
          'yyyy-MM-dd'
        );

        // Build query params
        const params = new URLSearchParams();
        params.append('startDate', startDate || defaultStartDate);
        params.append('endDate', endDate || defaultEndDate);
        
        // Fetch all required data in parallel
        const [
          totalExpensesData,
          averageCostData,
          profitabilityData,
          cashFlowData,
          mostProfitableData,
          pendingBalanceData,
          financialDashboardData
        ] = await Promise.all([
          makeRequest(`/reporting/expenses?${params.toString()}`),
          makeRequest(`/reporting/average-cost?${params.toString()}`),
          makeRequest(`/reporting/profitability?${params.toString()}`),
          makeRequest(`/reporting/cash-flow?${params.toString()}`),
          makeRequest(`/reporting/most-profitable?${params.toString()}`),
          makeRequest(`/reporting/pending-balance?${params.toString()}`),
          makeRequest(`/reporting/financial-dashboard?${params.toString()}`)
        ]);

        // Process monthly data for charts
        // Generate array for past 12 months
        const months = Array.from({ length: 12 }, (_, i) => {
          const d = new Date();
          d.setMonth(d.getMonth() - 11 + i);
          return format(d, 'yyyy-MM');
        });

        // Get monthly data from API or initialize empty arrays
        const gastosPorMes = totalExpensesData?.data?.monthlyExpenses || Array(12).fill(0);
        const rentabilidadPorMes = profitabilityData?.data?.monthlyProfitability || Array(12).fill(0);

        // Find month with highest expenses
        let mesMasGastos = "";
        if (gastosPorMes.length > 0 && Math.max(...gastosPorMes) > 0) {
          const maxGastosIdx = gastosPorMes.indexOf(Math.max(...gastosPorMes));
          const maxGastosMonth = new Date();
          maxGastosMonth.setMonth(maxGastosMonth.getMonth() - 11 + maxGastosIdx);
          mesMasGastos = format(maxGastosMonth, 'MMMM yyyy', { locale: es });
        }

        // Find month with highest profitability
        let mesMasRentable = "";
        if (rentabilidadPorMes.length > 0 && Math.max(...rentabilidadPorMes) > 0) {
          const maxRentabilidadIdx = rentabilidadPorMes.indexOf(Math.max(...rentabilidadPorMes));
          const maxRentabilidadMonth = new Date();
          maxRentabilidadMonth.setMonth(maxRentabilidadMonth.getMonth() - 11 + maxRentabilidadIdx);
          mesMasRentable = format(maxRentabilidadMonth, 'MMMM yyyy', { locale: es });
        }

        // Calculate trends
        const currentPeriodExpenses = totalExpensesData?.data?.totalExpenses || 0;
        const previousPeriodExpenses = totalExpensesData?.data?.previousPeriodExpenses || 0;
        const tendenciaGastos = previousPeriodExpenses > 0
          ? ((currentPeriodExpenses - previousPeriodExpenses) / previousPeriodExpenses) * 100
          : 0;

        const currentAverageCost = averageCostData?.data?.averageCost || financialDashboardData?.data?.averageCostPerExcursion || 0;
        const previousAverageCost = averageCostData?.data?.previousAverageCost || 0;
        const tendenciaCostoPromedio = previousAverageCost > 0
          ? ((currentAverageCost - previousAverageCost) / previousAverageCost) * 100
          : 0;

        const currentProfitability = profitabilityData?.data?.averageProfitability || 0;
        const previousProfitability = profitabilityData?.data?.previousProfitability || 0;
        const tendenciaRentabilidad = previousProfitability > 0
          ? currentProfitability - previousProfitability
          : 0;

        const currentCashFlow = cashFlowData?.data?.netCashFlow || financialDashboardData?.data?.netCashFlow || 0;
        const previousCashFlow = cashFlowData?.data?.previousNetCashFlow || 0;
        const tendenciaFlujoEfectivo = previousCashFlow > 0
          ? ((currentCashFlow - previousCashFlow) / previousCashFlow) * 100
          : 0;

        const currentPendingBalance = pendingBalanceData?.data?.pendingBalance || financialDashboardData?.data?.pendingBalance || 0;
        const previousPendingBalance = pendingBalanceData?.data?.previousPendingBalance || 0;
        const tendenciaSaldoPendiente = previousPendingBalance > 0
          ? ((currentPendingBalance - previousPendingBalance) / previousPendingBalance) * 100
          : 0;

        // Create distribution of expenses by category with colors
        const expensesByCategory = financialDashboardData?.data?.expensesByCategory || [];
        const colorMap: Record<string, string> = {
          "Combustible": "blue",
          "Guías": "green",
          "Mantenimiento": "orange",
          "Alimentación": "red",
          "Alojamiento": "purple",
          "Transporte": "yellow",
          "Seguros": "indigo",
          "Otros": "gray"
        };

        const distribucionGastos = expensesByCategory.map((item: any, index: number) => ({
          categoria: item.category || "Categoría " + (index + 1),
          monto: item.amount || 0,
          color: colorMap[item.category] || `gray`
        }));

        // Sort distribution by amount (highest first)
        distribucionGastos.sort((a: {categoria: string; monto: number; color: string}, b: {categoria: string; monto: number; color: string}) => b.monto - a.monto);

        return {
          gastosTotales: currentPeriodExpenses,
          costoPromedioExcursion: currentAverageCost,
          rentabilidadPromedio: currentProfitability,
          flujoEfectivoNeto: currentCashFlow,
          excursionMasRentable: {
            nombre: mostProfitableData?.data?.excursion?.name || "",
            rentabilidad: mostProfitableData?.data?.excursion?.profitability || 0
          },
          saldoPendienteCobro: currentPendingBalance,
          distribucionGastos,
          gastosPorMes,
          rentabilidadPorMes,
          tendenciaGastos: Math.round(tendenciaGastos * 10) / 10,
          tendenciaCostoPromedio: Math.round(tendenciaCostoPromedio * 10) / 10,
          tendenciaRentabilidad: Math.round(tendenciaRentabilidad * 10) / 10,
          tendenciaFlujoEfectivo: Math.round(tendenciaFlujoEfectivo * 10) / 10,
          tendenciaSaldoPendiente: Math.round(tendenciaSaldoPendiente * 10) / 10,
          mesMasGastos,
          mesMasRentable
        };
      } catch (error) {
        console.error("Error obteniendo estadísticas de gastos y rentabilidad:", error);
        throw error;
      }
    }
  }), []);

  // Funciones para OtrasMetricas
  const getAveragePersonsStats = async (startDate: string, endDate: string): Promise<AveragePersonsData> => {
    try {
      const response = await makeRequest(`/reporting/average-persons?startDate=${startDate}&endDate=${endDate}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching average persons data:", error);
      throw error;
    }
  };

  const getPaymentTimeStats = async (startDate: string, endDate: string): Promise<PaymentTimeData> => {
    try {
      const response = await makeRequest(`/reporting/payment-time?startDate=${startDate}&endDate=${endDate}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching payment time data:", error);
      throw error;
    }
  };

  const getPaymentMethodsStats = async (startDate: string, endDate: string): Promise<PaymentMethodsData> => {
    try {
      const response = await makeRequest(`/reporting/payment-methods?startDate=${startDate}&endDate=${endDate}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching payment methods data:", error);
      throw error;
    }
  };

  const getLastMinuteStats = async (startDate: string, endDate: string): Promise<LastMinuteData> => {
    try {
      const response = await makeRequest(`/reporting/last-minute?startDate=${startDate}&endDate=${endDate}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching last minute data:", error);
      throw error;
    }
  };

  const getAgencyCustomPrices = async (agencyId: number): Promise<any[]> => {
    const response = await makeRequest(`/agency-prices/agency/${agencyId}`);
    return response;
  };

  const getAgencyPricedExcursions = async (agencyId: number): Promise<any[]> => {
    try {
      const response = await makeRequest(`/agency-prices/agency/${agencyId}/excursions`);
      
      // Handle potential issues with the response
      if (!response) {
        console.warn('Empty response from getAgencyPricedExcursions');
        return [];
      }
      
      // If it's an array, return it directly
      if (Array.isArray(response)) {
        return response;
      }
      
      // If there's a data property that's an array, return that
      if (response.data && Array.isArray(response.data)) {
        return response.data;
      }
      
      // Otherwise, log the unexpected format and return an empty array
      console.warn('Unexpected response format from getAgencyPricedExcursions:', response);
      return [];
    } catch (error) {
      console.error("Error in getAgencyPricedExcursions:", error);
      throw error;
    }
  };

  const getCustomPrice = async (agencyId: number, excursionId: number): Promise<any> => {
    const response = await makeRequest(`/agency-prices/agency/${agencyId}/excursion/${excursionId}`);
    return response;
  };

  const createCustomPrice = async (priceData: { agencyId: number; excursionId: number; priceAdult: number; priceChild?: number | null }): Promise<any> => {
    const response = await makeRequest('/agency-prices', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(priceData),
    });
    return response;
  };

  const updateCustomPrice = async (id: number, priceData: { priceAdult: number; priceChild?: number | null }): Promise<any> => {
    const response = await makeRequest(`/agency-prices/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(priceData),
    });
    return response;
  };

  const deleteCustomPrice = async (id: number): Promise<void> => {
    await makeRequest(`/agency-prices/${id}`, {
      method: 'DELETE'
    });
  };

  const getAllExcursionsWithAgencyPrices = async (agencyId: number): Promise<any[]> => {
    // Since the functionality is the same and getAgencyPricedExcursions is already
    // using the correct endpoint, just use that function
    return getAgencyPricedExcursions(agencyId);
  };

  // Add a new function inside the useApi hook before the return statement
  const checkDuplicateReservations = async (
    params: Record<string, string>
  ): Promise<DuplicateReservationCheckResult> => {
    try {
      // Build query string from params
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });

      const response = await makeRequest(
        `/reservations/check-duplicate?${queryParams.toString()}`,
        {
          method: "GET",
          headers: getAuthHeaders()
        }
      );
      return response;
    } catch (error) {
      console.error("Error checking duplicate reservations:", error);
      throw error;
    }
  };

  // Register a new No Show
  const registerNoShow = async (data: {
    reservationId: number;
    missingAdults: number;
    missingChildren: number;
    justificacion?: string;
  }): Promise<{ message: string; noShow: NoShow; reservationUpdated: boolean }> => {
    try {
      const userName = getUserName();
      const response = await makeRequest(`/no-shows`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          ...data,
          registradoPor: userName
        }),
      });
      return response;
    } catch (error) {
      console.error('Error registering no show:', error);
      throw error;
    }
  };

  // Get all No Shows for a specific reservation
  const getReservationNoShows = async (reservationId: number): Promise<NoShow[]> => {
    try {
      return await makeRequest(`/no-shows/reservation/${reservationId}`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
    } catch (error) {
      console.error('Error getting reservation no shows:', error);
      throw error;
    }
  };

  // Delete a No Show record
  const deleteNoShow = async (id: number): Promise<{ message: string }> => {
    try {
      return await makeRequest(`/no-shows/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
    } catch (error) {
      console.error('Error deleting no show:', error);
      throw error;
    }
  };

  // Get user's name from localStorage JWT
  const getUserName = (): string => {
    const token = localStorage.getItem('token');
    if (!token) return 'Unknown User';
    
    try {
      // Extract payload from JWT token
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(window.atob(base64));
      
      // Return user's name or id if available
      return payload.name || payload.sub || 'Unknown User';
    } catch (error) {
      console.error('Error decoding JWT token:', error);
      return 'Unknown User';
    }
  };

  return useMemo(
    () => ({
      ...authMethods,
      ...excursionMethods,
      ...dashboardMethods,
      ...clientMethods,
      ...agencyMethods,
      ...reservationMethods,
      ...invoiceMethods,
      ...notificationMethods,
      ...utilityMethods,
      ...ingresoMethods,
      ...reportingMethods,
      formatCurrency,
      formatMetodoPago,
      getClientes,
      getAgencias,
      getNotas,
      getNotasByFactura,
      getNotaStats,
      createNota,
      updateNota,
      deleteNota,
      exportNotas,
      getGastos,
      getGastoStats,
      createGasto,
      updateGasto,
      deleteGasto,
      exportGastos,
      getAveragePersonsStats,
      getPaymentTimeStats,
      getPaymentMethodsStats,
      getLastMinuteStats,
      getAgencyCustomPrices,
      getAgencyPricedExcursions,
      getCustomPrice,
      createCustomPrice,
      updateCustomPrice,
      deleteCustomPrice,
      getAllExcursionsWithAgencyPrices,
      getReservation: async (id: number) => {
        return makeRequest(`/reservations/${id}`);
      },
      checkDuplicateReservations,
      // New utility function for API connection testing
      testApiConnection: testConnection,
      registerNoShow,
      getReservationNoShows,
      deleteNoShow,
    }),
    [
      authMethods,
      excursionMethods,
      dashboardMethods,
      clientMethods,
      agencyMethods,
      reservationMethods,
      invoiceMethods,
      notificationMethods,
      utilityMethods,
      ingresoMethods,
      reportingMethods,
      formatCurrency,
      formatMetodoPago,
      getClientes,
      getAgencias,
      getNotas,
      getNotasByFactura,
      getNotaStats,
      createNota,
      updateNota,
      deleteNota,
      exportNotas,
      getGastos,
      getGastoStats,
      createGasto,
      updateGasto,
      deleteGasto,
      exportGastos,
      getAveragePersonsStats,
      getPaymentTimeStats,
      getPaymentMethodsStats,
      getLastMinuteStats,
      getAgencyCustomPrices,
      getAgencyPricedExcursions,
      getCustomPrice,
      createCustomPrice,
      updateCustomPrice,
      deleteCustomPrice,
      getAllExcursionsWithAgencyPrices,
      checkDuplicateReservations,
      registerNoShow,
      getReservationNoShows,
      deleteNoShow,
    ]
  );
};

export default useApi;

// ----------------------------------------------------
// Exportaciones Adicionales
// ----------------------------------------------------
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const estadoDisplay: Record<ReservationStatus, string> = {
  pending_payment: "Pendiente",
  partial_payment: "Pago parcial",
  paid: "Pagada",
  no_show: "No Show",
  completed: "Completada"
} as const;

// Define types for clients and agencies
interface ClienteSimple {
  id: number;
  name: string;
  email: string;
  phone: string;
}

interface AgenciaSimple {
  id: number;
  name: string;
  email: string;
  phone: string;
}

// Nuevas interfaces para OtrasMetricas
export interface PaymentMethod {
  method: string;
  count: number;
  amount: number;
  countPercentage: number;
  amountPercentage: number;
}

export interface AdvanceDistributionItem {
  count: number;
  percentage: number;
  revenue: number;
}

export interface AdvanceDistribution {
  [key: string]: AdvanceDistributionItem;
}

export interface MonthlyDistributionItem {
  month: string;
  reservations: number;
  persons: number;
  average: number;
}

export interface AveragePersonsData {
  averagePersonsPerReservation: number;
  averageAdultsPerReservation: number;
  averageChildrenPerReservation: number;
  totalReservations: number;
  totalPersons: number;
  totalAdults: number;
  totalChildren: number;
  monthlyDistribution: MonthlyDistributionItem[];
}

export interface PaymentTimeData {
  averagePaymentTime: number;
  medianPaymentTime: number;
  totalReservationsWithPayments: number;
  paymentTimeDistribution: {
    [key: string]: number;
  };
  paymentTimeDistributionPercentage: {
    [key: string]: number;
  };
}

export interface PaymentMethodsData {
  paymentMethods: PaymentMethod[];
  totalTransactions: number;
  totalAmount: number;
}

export interface LastMinuteData {
  lastMinuteThresholdDays: number;
  lastMinuteCount: number;
  regularCount: number;
  totalReservations: number;
  lastMinutePercentage: number;
  advanceDistribution: AdvanceDistribution;
}

export interface NoShow {
  id: number;
  reservationId: number;
  missingAdults: number;
  missingChildren: number;
  fechaRegistro: string;
  justificacion?: string;
  registradoPor: string;
  reservation?: {
    excursion: string;
    fecha: string;
    hotel: string;
    adultos: number;
    ninios: number;
    estado: ReservationStatus;
  };
}

// Add this function before the useApi hook definition
/**
 * Test connection to the API server
 * Returns a promise that resolves with connection details or rejects with error
 */
export async function testConnection() {
  try {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    console.log(`Testing connection to API server at: ${apiUrl}`);
    
    // Test with a simple endpoint that doesn't require authentication
    const response = await fetch(`${apiUrl}/health`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const errorDetails = await response.text();
      console.error(`API server responded with status ${response.status}: ${errorDetails}`);
      return {
        success: false,
        status: response.status,
        statusText: response.statusText,
        details: errorDetails,
        url: apiUrl
      };
    }
    
    const data = await response.json();
    return {
      success: true,
      status: response.status,
      data,
      url: apiUrl
    };
  } catch (error) {
    console.error('Error connecting to API server:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
      url: process.env.REACT_APP_API_URL || 'http://localhost:5000/api'
    };
  }
}